/* @flow */
import * as React from 'react';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import Moment from 'react-moment';
import ListItem from '@material-ui/core/ListItem';
import type { TranslatorProps } from 'react-i18next';

import { SENT_REQUEST_TYPES, generateValues, getNotificationCopy } from 'utils/NotificationUtils';
import { errorHandlerService } from 'services/ServiceRegistry';

import styles from './SentRequestTableItem.module.scss';

type TypeProps = {|
  notification: Object,
|} & TranslatorProps;

/**
 * This object defines which Sent Requests have a Reason and which do not.
 */
const reason = ({ t, data }) => ({
  'Request Asset Rental Extension': reason => <div>Reason: {reason}</div>,
  'Request Asset Ownership': reason => <div>Reason: {reason}</div>,
  'Request Asset Allocation To Project': reason => <div>Reason: {reason}</div>,
  'Request Contract Extension': reason => <div>Reason: {reason}</div>,
  'Asset Fault Report': reason => <div>Fault Report: {reason}</div>,
  'Asset Calibration Request': reason => <div />,
  'Quote Request': reason => <div />,
});

@translate()
@observer
export default class SentRequestTableItem extends React.Component<*> {
  @observable isExpanded = false;

  @computed get title(): string {
    const { notification, t } = this.props;

    if (notification.action_name === SENT_REQUEST_TYPES.NEW_LICENSE_REQUEST) {
      const values = generateValues(notification);
      const titleCopyInfo = getNotificationCopy(values.notification, 'Title');

      return t(titleCopyInfo.key);
    }

    return notification.action_name;
  }

  render(): React.Element<*> {
    const { t, notification }: TypeProps = this.props;

    try {
      return (
        <Link to={`/sent-requests/${notification.id}`}>
          <ListItem button={true} className={styles['noti-table-item']}>
            <div className={styles['noti-item-icon']}>
              <svg>
                <use xlinkHref="#icon-assets" />
              </svg>
              <svg className={`${styles['status-icon']} ${styles['status-requested']}`}>
                <use xlinkHref="#icon-notification-action" />
              </svg>
            </div>

            <div className={styles['noti-item-desc']}>
              <div className={styles['desc-top-line']}>
                <span>{this.title}</span>
                <small>
                  <Moment date={notification.created_at} format="MMMM DD, YYYY - hh:mm a" />
                </small>
              </div>

              <div className={styles['desc-bottom-line']}>
                {notification.details.contract && (
                  <ContractDetails t={t} notification={notification} />
                )}

                {notification.details.asset && (
                  <AssetDetails
                    t={t}
                    notification={notification}
                    asset={notification.details.asset}
                  />
                )}

                {notification.details.help_with ? (
                  <div>
                    {t('general:notifications.requested-support-for')}:{' '}
                    {`${notification.details.help_with}`}
                  </div>
                ) : null}

                {notification.action_name === 'New Asset Request' && (
                  <React.Fragment>
                    <div>{notification.details.asset_request.unique_asset_request_id}</div>
                    <div>{notification.user_name}</div>
                  </React.Fragment>
                )}

                {notification.action_name === SENT_REQUEST_TYPES.NEW_LICENSE_REQUEST && (
                  <LicenseRequestDetails t={t} notification={notification} />
                )}
              </div>
            </div>

            <div>
              <svg className={styles['view-icon']}>
                <use xlinkHref="#icon-chevron-down" />
              </svg>
            </div>
          </ListItem>
        </Link>
      );
    } catch (err) {
      errorHandlerService.handleException(err);

      return <div>Exception</div>;
    }
  }
}

export const ContractDetails = ({ notification, t }) => {
  return (
    <div>
      {t('general:account')}:&nbsp;
      {notification.account_name}
      <br />
      {t('general:contract')}:&nbsp;
      {notification.details.contract.er_contract_number}
      <br />
      {t('general:labels.extended_to')}:{' '}
      <Moment
        date={notification.details.contract.new_contract_end_date}
        format="MMMM DD, YYYY - hh:mm a"
      />
      <br />
      {reason(t, '')[notification.action_name](
        notification.details.reason || notification.details.fault_details
      )}
    </div>
  );
};

export const AssetDetails = ({ notification, asset, t }) => (
  <div>
    {t('general:account')}: {notification.account_name}
    <br />
    {t('general:asset')}: {asset.asset_number}
    <br />
    <div>
      {`${t('general:manufacturer')}: ${asset.manufacturer}`}
      <br />
      {`${t('general:model')}: ${asset.model}`}
    </div>
    {/*ADD EXTRA REASON*/}
    {notification.action_name === SENT_REQUEST_TYPES.ASSET_RENTAL_EXTENSION && (
      <div>
        {t('general:labels.extended_to')}:{' '}
        <Moment
          date={notification.details.contract.new_contract_end_date}
          format="MMMM DD, YYYY - hh:mm a"
        />
        <br />
        {t('general:reason')}: {notification.details.reason}
      </div>
    )}
    {/*ADD CALIBRATION DATE*/}
    {notification.action_name === SENT_REQUEST_TYPES.ASSET_CALIBRATION && (
      <div>
        {t('general:labels.due_date')}:{' '}
        <Moment date={asset.er_calibration_due_date} format="MMMM DD, YYYY - hh:mm a" />
      </div>
    )}
    {notification.action_name === SENT_REQUEST_TYPES.ASSET_FAULT_REPORT && (
      <div>
        {t('general:assets.labels.fault_details')}: {notification.details.fault_details}
      </div>
    )}
  </div>
);

export const AssetRequestDetails = ({ notification, t }) => (
  <React.Fragment>
    <div>
      <b>{t('general:labels.request_id')}: </b>{' '}
      {notification.details.asset_request.unique_asset_request_id}
    </div>
    <div>
      <b>{t('general:account')}: </b> {notification.account_name}
    </div>
    <div>
      <b>{t('general:labels.requested_by')}: </b> {notification.user_name}
    </div>
    <div>
      <b>{t('general:labels.date_requested')}: </b>
      {<Moment date={notification.created_at} format="MMMM DD, YYYY" />}
    </div>
  </React.Fragment>
);

export const LicenseRequestDetails = ({ notification, t }) => (
  <React.Fragment>
    <div>
      <b>{t('general:labels.request_id')}: </b>{' '}
      {notification.details.asset_request.unique_license_request_id}
    </div>
    <div>
      <b>{t('general:account')}: </b> {notification.account_name}
    </div>
    <div>
      <b>{t('general:labels.requested_by')}: </b> {notification.user_name}
    </div>
  </React.Fragment>
);
